import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const API_BASE = import.meta.env.VITE_API_BASE || "http://localhost:5000";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');

  :root {
    --black:#0a0a0a; --deep:#111111; --card:#161616;
    --border:#2a2a2a; --gold:#c9a84c; --gold-light:#e2c36e;
    --white:#f5f0e8; --grey:#888888; --grey-light:#cccccc;
    --green:#25D366; --red:#e05252; --input-bg:#1a1a1a;
  }
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  body { background:var(--black); color:var(--white); font-family:'Montserrat',sans-serif; font-weight:300; }

  @keyframes fadeUp  { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  @keyframes pop     { 0%{transform:scale(0.8);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
  @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.5} }

  /* ── HEADER ── */
  .pay-header {
    background:linear-gradient(160deg,#0d0d0d 0%,#111 60%,#0a0a0a 100%);
    border-bottom:1px solid var(--border);
    padding:100px 60px 36px; position:relative; overflow:hidden;
  }
  .pay-header::before {
    content:''; position:absolute; inset:0;
    background:radial-gradient(ellipse 50% 100% at 80% 50%,rgba(201,168,76,0.07) 0%,transparent 70%);
    pointer-events:none;
  }
  .pay-breadcrumb {
    font-size:0.62rem; letter-spacing:0.2em; text-transform:uppercase;
    color:var(--grey); margin-bottom:14px;
  }
  .pay-breadcrumb span { color:var(--gold); }
  .pay-title {
    font-family:'Cormorant Garamond',serif;
    font-size:clamp(1.8rem,3vw,2.8rem); font-weight:300; color:var(--white);
  }
  .pay-title em { font-style:italic; color:var(--gold); }

  /* ── BODY ── */
  .pay-body {
    display:grid; grid-template-columns:1fr 360px;
    gap:28px; padding:40px 60px 80px; align-items:start;
  }

  /* ── STEP INDICATOR ── */
  .steps-bar {
    display:flex; align-items:center; gap:0;
    background:var(--card); border:1px solid var(--border);
    margin-bottom:24px; animation:fadeUp 0.4s ease both;
  }
  .step-item {
    flex:1; padding:14px 16px; display:flex; align-items:center; gap:10px;
    border-right:1px solid var(--border); position:relative;
  }
  .step-item:last-child { border-right:none; }
  .step-num {
    width:26px; height:26px; border-radius:50%; border:1px solid var(--border);
    display:flex; align-items:center; justify-content:center;
    font-size:0.6rem; font-weight:600; color:var(--grey); flex-shrink:0;
  }
  .step-item.done .step-num {
    background:rgba(201,168,76,0.15); border-color:var(--gold); color:var(--gold);
  }
  .step-item.active .step-num {
    background:var(--gold); border-color:var(--gold); color:var(--black);
  }
  .step-label { font-size:0.58rem; letter-spacing:0.12em; text-transform:uppercase; color:var(--grey); }
  .step-item.done .step-label { color:var(--gold); }
  .step-item.active .step-label { color:var(--white); }

  /* ── CARDS ── */
  .pay-card {
    background:var(--card); border:1px solid var(--border);
    position:relative; margin-bottom:20px;
    animation:fadeUp 0.5s ease both;
  }
  .pay-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:2px;
    background:linear-gradient(90deg,transparent,var(--gold),transparent);
    background-size:200% 100%; animation:shimmer 3s ease infinite;
  }
  .pay-card-head {
    padding:16px 24px; border-bottom:1px solid var(--border);
    display:flex; align-items:center; gap:12px;
  }
  .pay-card-icon {
    width:32px; height:32px; background:rgba(201,168,76,0.1);
    border:1px solid rgba(201,168,76,0.3);
    display:flex; align-items:center; justify-content:center; font-size:0.85rem;
  }
  .pay-card-title {
    font-size:0.6rem; letter-spacing:0.28em; text-transform:uppercase; color:var(--gold);
  }

  /* ── PAYMENT METHOD TABS ── */
  .method-tabs {
    display:grid; grid-template-columns:repeat(3,1fr);
    gap:0; border-bottom:1px solid var(--border);
  }
  .method-tab {
    padding:16px 12px; cursor:pointer; border-right:1px solid var(--border);
    display:flex; flex-direction:column; align-items:center; gap:6px;
    transition:all 0.25s; background:transparent; border-top:none; border-left:none; border-bottom:none;
    font-family:'Montserrat',sans-serif;
  }
  .method-tab:last-child { border-right:none; }
  .method-tab.active { background:rgba(201,168,76,0.06); }
  .method-tab-icon { font-size:1.2rem; }
  .method-tab-label {
    font-size:0.55rem; letter-spacing:0.15em; text-transform:uppercase;
    color:var(--grey); transition:color 0.25s;
  }
  .method-tab.active .method-tab-label { color:var(--gold); }
  .method-tab-indicator {
    width:24px; height:2px; background:transparent; transition:background 0.25s;
    margin-top:2px;
  }
  .method-tab.active .method-tab-indicator { background:var(--gold); }

  /* ── FORM ── */
  .form-body { padding:24px; }
  .form-row { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:16px; }
  .form-row.full { grid-template-columns:1fr; }
  .form-row.triple { grid-template-columns:1fr 1fr 1fr; }
  .form-group { display:flex; flex-direction:column; gap:7px; }
  .form-label {
    font-size:0.55rem; letter-spacing:0.18em; text-transform:uppercase; color:var(--grey);
  }
  .form-input {
    background:var(--input-bg); border:1px solid var(--border); color:var(--white);
    padding:12px 14px; font-family:'Montserrat',sans-serif; font-size:0.78rem;
    font-weight:300; outline:none; transition:border-color 0.25s;
    -webkit-appearance:none;
  }
  .form-input::placeholder { color:#444; }
  .form-input:focus { border-color:var(--gold); }
  .form-input.error { border-color:var(--red); }

  /* card number with icons */
  .card-input-wrap { position:relative; }
  .card-input-wrap .form-input { padding-right:90px; }
  .card-brands {
    position:absolute; right:12px; top:50%; transform:translateY(-50%);
    display:flex; gap:5px; align-items:center;
  }
  .card-brand {
    width:32px; height:20px; border:1px solid var(--border);
    background:#1e1e1e; display:flex; align-items:center; justify-content:center;
    font-size:0.45rem; color:var(--grey); font-weight:700; letter-spacing:0.05em;
    opacity:0.5; transition:opacity 0.2s;
  }
  .card-brand.active { opacity:1; border-color:var(--gold); color:var(--gold-light); }

  /* security badge */
  .security-note {
    display:flex; align-items:center; gap:8px;
    padding:12px 24px; background:rgba(201,168,76,0.03);
    border-top:1px solid var(--border);
    font-size:0.6rem; color:var(--grey); letter-spacing:0.08em;
  }
  .security-note svg { opacity:0.5; }

  /* ── PAY BUTTON ── */
  .pay-now-btn {
    width:100%; padding:18px; background:var(--gold); border:none;
    color:var(--black); font-family:'Montserrat',sans-serif;
    font-size:0.78rem; font-weight:700; letter-spacing:0.25em;
    text-transform:uppercase; cursor:pointer; transition:all 0.3s;
    display:flex; align-items:center; justify-content:center; gap:10px;
    margin-top:20px;
  }
  .pay-now-btn:hover:not(:disabled) { background:var(--gold-light); letter-spacing:0.32em; }
  .pay-now-btn:disabled { background:#2a2a2a; color:#555; cursor:not-allowed; }

  .pay-later-btn {
    width:100%; padding:14px; background:transparent;
    border:1px solid var(--border); color:var(--grey-light);
    font-family:'Montserrat',sans-serif; font-size:0.68rem; font-weight:500;
    letter-spacing:0.2em; text-transform:uppercase; cursor:pointer;
    transition:all 0.3s; margin-top:10px;
    display:flex; align-items:center; justify-content:center; gap:8px;
  }
  .pay-later-btn:hover:not(:disabled) { border-color:var(--gold); color:var(--gold); }
  .pay-later-btn:disabled { opacity:0.4; cursor:not-allowed; }

  /* ── SIDEBAR ── */
  .pay-sidebar { display:flex; flex-direction:column; gap:16px; }
  .side-card {
    background:var(--card); border:1px solid var(--border); position:relative;
  }
  .side-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:2px;
    background:linear-gradient(90deg,transparent,var(--gold),transparent);
    background-size:200% 100%; animation:shimmer 3s ease infinite;
  }
  .side-head {
    padding:14px 20px; border-bottom:1px solid var(--border);
    font-size:0.6rem; letter-spacing:0.25em; text-transform:uppercase; color:var(--gold);
  }
  .side-rows { padding:8px 0; }
  .side-row {
    display:flex; justify-content:space-between; align-items:center;
    padding:9px 20px; border-bottom:1px solid var(--border);
    font-size:0.7rem;
  }
  .side-row:last-child { border-bottom:none; }
  .side-row-label { color:var(--grey); }
  .side-row-val { color:var(--white); font-weight:500; }
  .side-row-val.gold {
    font-family:'Cormorant Garamond',serif; font-size:1.1rem; color:var(--gold);
  }
  .side-row-val.big {
    font-family:'Cormorant Garamond',serif; font-size:1.5rem; color:var(--gold);
  }
  .side-divider { height:1px; background:var(--border); margin:4px 0; }

  /* car mini strip */
  .car-mini {
    display:flex; align-items:center; gap:14px;
    padding:16px 20px; border-bottom:1px solid var(--border);
  }
  .car-mini-img {
    width:70px; height:50px; background:#111; flex-shrink:0; overflow:hidden;
  }
  .car-mini-img img { width:100%; height:100%; object-fit:contain; padding:4px; }
  .car-mini-name {
    font-family:'Cormorant Garamond',serif; font-size:1.1rem; color:var(--white);
  }
  .car-mini-sub { font-size:0.6rem; color:var(--grey); margin-top:3px; }

  /* trust badges */
  .trust-grid {
    display:grid; grid-template-columns:1fr 1fr; gap:1px;
    background:var(--border); margin-top:1px;
  }
  .trust-item {
    background:var(--card); padding:14px 16px;
    display:flex; flex-direction:column; align-items:center; gap:5px; text-align:center;
  }
  .trust-icon { font-size:1.1rem; }
  .trust-label { font-size:0.55rem; letter-spacing:0.1em; text-transform:uppercase; color:var(--grey); line-height:1.4; }

  /* processing overlay */
  .processing-overlay {
    position:fixed; inset:0; background:rgba(0,0,0,0.9); z-index:400;
    display:flex; flex-direction:column; align-items:center; justify-content:center; gap:20px;
  }
  .processing-ring {
    width:64px; height:64px; border:3px solid var(--border);
    border-top-color:var(--gold); border-radius:50%;
    animation:spin 0.8s linear infinite;
  }
  .processing-text {
    font-size:0.7rem; letter-spacing:0.25em; text-transform:uppercase; color:var(--grey);
    animation:pulse 1.5s ease infinite;
  }

  /* M-Pesa / Bank forms */
  .mpesa-info {
    padding:24px; display:flex; flex-direction:column; gap:16px;
  }
  .mpesa-step {
    display:flex; gap:14px; align-items:flex-start;
  }
  .mpesa-num {
    width:28px; height:28px; border-radius:50%;
    background:rgba(201,168,76,0.1); border:1px solid rgba(201,168,76,0.3);
    display:flex; align-items:center; justify-content:center;
    font-size:0.65rem; color:var(--gold); font-weight:600; flex-shrink:0;
  }
  .mpesa-step-text { font-size:0.72rem; color:var(--grey-light); line-height:1.6; }
  .mpesa-step-text strong { color:var(--white); }
  .mpesa-ref-input {
    background:var(--input-bg); border:1px solid var(--border); color:var(--white);
    padding:12px 14px; font-family:'Montserrat',sans-serif; font-size:0.78rem;
    outline:none; transition:border-color 0.25s; width:100%;
  }
  .mpesa-ref-input:focus { border-color:var(--gold); }
  .mpesa-ref-label {
    font-size:0.55rem; letter-spacing:0.18em; text-transform:uppercase; color:var(--grey); margin-bottom:7px;
  }

  @media (max-width:1000px) {
    .pay-body { grid-template-columns:1fr; padding:30px; }
    .pay-header { padding:100px 30px 30px; }
  }
  @media (max-width:600px) {
    .pay-header { padding:90px 20px 24px; }
    .pay-body { padding:20px; }
    .form-row { grid-template-columns:1fr; }
    .form-row.triple { grid-template-columns:1fr 1fr; }
    .method-tabs { grid-template-columns:1fr 1fr 1fr; }
    .steps-bar { overflow-x:auto; }
  }
`;

function formatCard(val) {
  return val.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
}
function formatExpiry(val) {
  const v = val.replace(/\D/g, "").slice(0, 4);
  return v.length > 2 ? v.slice(0, 2) + "/" + v.slice(2) : v;
}

export default function TransferPayment() {
  const { state }  = useLocation();
  const navigate   = useNavigate();

  const [method,   setMethod]  = useState("card"); // card | mpesa | bank
  const [paying,   setPaying]  = useState(false);
  const [processing, setProcessing] = useState(false);

  // Card fields
  const [cardName,   setCardName]   = useState("");
  const [cardNum,    setCardNum]    = useState("");
  const [expiry,     setExpiry]     = useState("");
  const [cvv,        setCvv]        = useState("");
  const [cardErrors, setCardErrors] = useState({});

  // M-Pesa
  const [mpesaRef,   setMpesaRef]   = useState("");
  const [mpesaPhone, setMpesaPhone] = useState("");

  useEffect(() => {
    const id = "pay-styles";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id; s.textContent = CSS;
      document.head.appendChild(s);
    }
    return () => document.getElementById(id)?.remove();
  }, []);

  if (!state) { navigate("/"); return null; }

  const { car = {}, total_price, finalKm, oneway, email, firstName, lastName, bookingRef } = state;
  const currency  = "$";
  const basePrice = parseFloat(car.base_price) || 0;
  const ratePerKm = parseFloat(car.trnsfer_price) || 0;
  const totalKm   = parseFloat((finalKm * ratePerKm).toFixed(2));
  const estimated = parseFloat(total_price) || parseFloat((basePrice + totalKm).toFixed(2));
  const rounded   = Math.round(estimated);
  const advance   = parseFloat((rounded / 2).toFixed(2));

  // detect card brand
  const cardBrand = () => {
    const n = cardNum.replace(/\s/g, "");
    if (/^4/.test(n)) return "VISA";
    if (/^5[1-5]/.test(n)) return "MC";
    if (/^3[47]/.test(n)) return "AMEX";
    return null;
  };

  const validateCard = () => {
    const errs = {};
    if (!cardName.trim()) errs.cardName = true;
    if (cardNum.replace(/\s/g, "").length < 16) errs.cardNum = true;
    if (expiry.length < 5) errs.expiry = true;
    if (cvv.length < 3) errs.cvv = true;
    setCardErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handlePayNow = async () => {
    if (method === "card" && !validateCard()) return;
    if (method === "mpesa" && !mpesaRef.trim()) {
      alert("Please enter your M-Pesa transaction reference.");
      return;
    }

    setProcessing(true);
    try {
      const res  = await fetch(`${API_BASE}/api/transfer/confirm`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          ...state,
          confirmed: true,
          paymentMethod: method,
          paymentStatus: "paid",
          amountPaid: advance,
          ...(method === "card"  ? { cardLast4: cardNum.replace(/\s/g, "").slice(-4) } : {}),
          ...(method === "mpesa" ? { mpesaRef, mpesaPhone } : {}),
        }),
      });
      const data = await res.json();
      const ref  = data.bookingRef || bookingRef || "TRF-" + Date.now();
      // Navigate to confirmation page
      navigate("/transfer-confirmation", {
        state: { ...state, bookingRef: ref, paymentStatus: "paid", amountPaid: advance },
      });
    } catch {
      // Even on network error, go to confirmation (offline fallback)
      navigate("/transfer-confirmation", {
        state: { ...state, bookingRef: bookingRef || "TRF-" + Date.now(), paymentStatus: "paid", amountPaid: advance },
      });
    } finally {
      setProcessing(false);
    }
  };

  const handlePayLater = async () => {
    setPaying(true);
    try {
      const res  = await fetch(`${API_BASE}/api/transfer/confirm`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          ...state,
          confirmed: true,
          paymentMethod: "pay_later",
          paymentStatus: "pending",
          amountPaid: 0,
        }),
      });
      const data = await res.json();
      const ref  = data.bookingRef || bookingRef || "TRF-" + Date.now();
      navigate("/transfer-confirmation", {
        state: { ...state, bookingRef: ref, paymentStatus: "pending", amountPaid: 0 },
      });
    } catch {
      navigate("/transfer-confirmation", {
        state: { ...state, bookingRef: bookingRef || "TRF-" + Date.now(), paymentStatus: "pending", amountPaid: 0 },
      });
    } finally {
      setPaying(false);
    }
  };

  const brand = cardBrand();

  return (
    <div>
      <Navbar activePage="Book a Taxi" />

      {/* Processing overlay */}
      {processing && (
        <div className="processing-overlay">
          <div className="processing-ring" />
          <div className="processing-text">Processing your payment…</div>
        </div>
      )}

      {/* Header */}
      <div className="pay-header">
        <div className="pay-breadcrumb">
          Home <span>›</span> Book a Taxi <span>›</span> Vehicles <span>›</span>
          Passenger Details <span>›</span> Review <span>›</span> <span>Payment</span>
        </div>
        <h1 className="pay-title">Secure <em>Payment</em></h1>
      </div>

      <div className="pay-body">
        {/* LEFT */}
        <div>
          {/* Step bar */}
          <div className="steps-bar">
            {[
              { label:"Search",    n:1 },
              { label:"Vehicle",   n:2 },
              { label:"Details",   n:3 },
              { label:"Review",    n:4 },
              { label:"Payment",   n:5 },
            ].map(s => (
              <div key={s.n} className={`step-item ${s.n < 5 ? "done" : "active"}`}>
                <div className="step-num">{s.n < 5 ? "✓" : s.n}</div>
                <div className="step-label">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Method selector */}
          <div className="pay-card">
            <div className="pay-card-head">
              <div className="pay-card-icon">💳</div>
              <div className="pay-card-title">Choose Payment Method</div>
            </div>
            <div className="method-tabs">
              {[
                { id:"card",  icon:"💳", label:"Credit / Debit Card" },
                { id:"mpesa", icon:"📱", label:"M-Pesa" },
                { id:"bank",  icon:"🏦", label:"Bank Transfer" },
              ].map(m => (
                <button
                  key={m.id}
                  className={`method-tab ${method === m.id ? "active" : ""}`}
                  onClick={() => setMethod(m.id)}
                >
                  <div className="method-tab-icon">{m.icon}</div>
                  <div className="method-tab-label">{m.label}</div>
                  <div className="method-tab-indicator" />
                </button>
              ))}
            </div>

            {/* ── CARD FORM ── */}
            {method === "card" && (
              <>
                <div className="form-body">
                  <div className="form-row full">
                    <div className="form-group">
                      <label className="form-label">Cardholder Name</label>
                      <input
                        className={`form-input ${cardErrors.cardName ? "error" : ""}`}
                        placeholder="As printed on card"
                        value={cardName}
                        onChange={e => { setCardName(e.target.value); setCardErrors(p => ({ ...p, cardName: false })); }}
                      />
                    </div>
                  </div>
                  <div className="form-row full">
                    <div className="form-group">
                      <label className="form-label">Card Number</label>
                      <div className="card-input-wrap">
                        <input
                          className={`form-input ${cardErrors.cardNum ? "error" : ""}`}
                          placeholder="0000 0000 0000 0000"
                          value={cardNum}
                          onChange={e => { setCardNum(formatCard(e.target.value)); setCardErrors(p => ({ ...p, cardNum: false })); }}
                          inputMode="numeric"
                        />
                        <div className="card-brands">
                          <div className={`card-brand ${brand === "VISA" ? "active" : ""}`}>VISA</div>
                          <div className={`card-brand ${brand === "MC"   ? "active" : ""}`}>MC</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-row triple">
                    <div className="form-group" style={{ gridColumn:"1/3" }}>
                      <label className="form-label">Expiry Date</label>
                      <input
                        className={`form-input ${cardErrors.expiry ? "error" : ""}`}
                        placeholder="MM / YY"
                        value={expiry}
                        onChange={e => { setExpiry(formatExpiry(e.target.value)); setCardErrors(p => ({ ...p, expiry: false })); }}
                        inputMode="numeric"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">CVV / CVC</label>
                      <input
                        className={`form-input ${cardErrors.cvv ? "error" : ""}`}
                        placeholder="···"
                        value={cvv}
                        maxLength={4}
                        onChange={e => { setCvv(e.target.value.replace(/\D/g, "").slice(0, 4)); setCardErrors(p => ({ ...p, cvv: false })); }}
                        inputMode="numeric"
                        type="password"
                      />
                    </div>
                  </div>
                </div>
                <div className="security-note">
                  <svg width="12" height="14" fill="none" viewBox="0 0 12 14">
                    <rect x="1" y="5" width="10" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                    <path d="M4 5V3.5a2 2 0 014 0V5" stroke="currentColor" strokeWidth="1.2"/>
                  </svg>
                  256-bit SSL encrypted · Your card details are never stored
                </div>
              </>
            )}

            {/* ── M-PESA FORM ── */}
            {method === "mpesa" && (
              <div className="mpesa-info">
                <div className="mpesa-step">
                  <div className="mpesa-num">1</div>
                  <div className="mpesa-step-text">
                    Go to <strong>M-Pesa</strong> on your phone → <strong>Lipa na M-Pesa</strong> → <strong>Pay Bill</strong>
                  </div>
                </div>
                <div className="mpesa-step">
                  <div className="mpesa-num">2</div>
                  <div className="mpesa-step-text">
                    Business No: <strong>123456</strong> · Account No: <strong>{bookingRef || "ZTR-BOOKING"}</strong>
                  </div>
                </div>
                <div className="mpesa-step">
                  <div className="mpesa-num">3</div>
                  <div className="mpesa-step-text">
                    Enter amount: <strong>{currency}{advance.toFixed(2)}</strong> and complete payment
                  </div>
                </div>
                <div className="mpesa-step">
                  <div className="mpesa-num">4</div>
                  <div className="mpesa-step-text">Enter your M-Pesa confirmation details below</div>
                </div>
                <div>
                  <div className="mpesa-ref-label">Your M-Pesa Phone Number</div>
                  <input className="mpesa-ref-input" placeholder="+255 7XX XXX XXX"
                    value={mpesaPhone} onChange={e => setMpesaPhone(e.target.value)} />
                </div>
                <div>
                  <div className="mpesa-ref-label">M-Pesa Transaction Reference</div>
                  <input className="mpesa-ref-input" placeholder="e.g. QHX7Y3ABCD"
                    value={mpesaRef} onChange={e => setMpesaRef(e.target.value.toUpperCase())} />
                </div>
              </div>
            )}

            {/* ── BANK TRANSFER ── */}
            {method === "bank" && (
              <div className="mpesa-info">
                <div className="mpesa-step">
                  <div className="mpesa-num">1</div>
                  <div className="mpesa-step-text">
                    Transfer <strong>{currency}{advance.toFixed(2)}</strong> to our bank account
                  </div>
                </div>
                {[
                  { l:"Bank Name",    v:"CRDB Bank Tanzania" },
                  { l:"Account Name", v:"ZanzibarTransfer Ltd" },
                  { l:"Account No",   v:"01J2345678901" },
                  { l:"SWIFT / BIC",  v:"CORUTZTZ" },
                  { l:"Reference",    v:bookingRef || "ZTR-BOOKING" },
                ].map(r => (
                  <div key={r.l} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:"1px solid var(--border)", fontSize:"0.72rem" }}>
                    <span style={{ color:"var(--grey)" }}>{r.l}</span>
                    <span style={{ color:"var(--white)", fontWeight:500 }}>{r.v}</span>
                  </div>
                ))}
                <div style={{ fontSize:"0.65rem", color:"var(--grey)", lineHeight:1.7, marginTop:6 }}>
                  After transfer, click confirm below. Our team will verify within 24 hours.
                </div>
              </div>
            )}

            <div style={{ padding:"0 24px 24px" }}>
              <button className="pay-now-btn" onClick={handlePayNow} disabled={processing || paying}>
                {processing ? (
                  <>
                    <span style={{ width:16, height:16, border:"2px solid #000", borderTopColor:"transparent", borderRadius:"50%", display:"inline-block", animation:"spin 0.7s linear infinite" }} />
                    Processing…
                  </>
                ) : (
                  <>🔒 Pay {currency}{advance.toFixed(2)} Now</>
                )}
              </button>

              <button className="pay-later-btn" onClick={handlePayLater} disabled={processing || paying}>
                {paying ? "Submitting…" : "⏱ Pay Later — Submit Booking Only"}
              </button>

              <p style={{ fontSize:"0.6rem", color:"var(--grey)", textAlign:"center", marginTop:12, lineHeight:1.6 }}>
                {method === "card"
                  ? `You'll be charged ${currency}${advance.toFixed(2)} (50% advance). Remaining ${currency}${(rounded - advance).toFixed(2)} on arrival.`
                  : method === "mpesa"
                  ? `Complete M-Pesa payment first, then submit your reference above.`
                  : `Bank transfer must clear before your booking is fully confirmed.`
                }
              </p>
            </div>
          </div>
        </div>

        {/* SIDEBAR */}
        <div className="pay-sidebar">
          {/* Booking Summary */}
          <div className="side-card">
            <div className="side-head">Booking Summary</div>
            <div className="car-mini">
              <div className="car-mini-img">
                <img src={`${API_BASE}/${car.car_image}`} alt={car.car_name}
                  onError={e => { e.target.style.display = "none"; }} />
              </div>
              <div>
                <div className="car-mini-name">{car.car_name}</div>
                <div className="car-mini-sub">👥 {car.passengers} pax · 🧳 {car.luggage} bags</div>
              </div>
            </div>
            <div className="side-rows">
              <div className="side-row">
                <span className="side-row-label">Passenger</span>
                <span className="side-row-val">{firstName} {lastName}</span>
              </div>
              <div className="side-row">
                <span className="side-row-label">Trip Type</span>
                <span className="side-row-val">{oneway === "1" ? "One Way" : "Two Way"}</span>
              </div>
              <div className="side-row">
                <span className="side-row-label">Distance</span>
                <span className="side-row-val">{finalKm} KM</span>
              </div>
              <div className="side-divider" />
              <div className="side-row">
                <span className="side-row-label">Base Rate</span>
                <span className="side-row-val">{currency}{basePrice.toFixed(2)}</span>
              </div>
              <div className="side-row">
                <span className="side-row-label">KM Charges</span>
                <span className="side-row-val">{currency}{totalKm.toFixed(2)}</span>
              </div>
              <div className="side-row">
                <span className="side-row-label">Total</span>
                <span className="side-row-val gold">{currency}{estimated.toFixed(2)}</span>
              </div>
              <div className="side-divider" />
              <div className="side-row" style={{ background:"rgba(201,168,76,0.04)" }}>
                <span className="side-row-label" style={{ fontWeight:600, color:"var(--white)" }}>Pay Now (50%)</span>
                <span className="side-row-val big">{currency}{advance.toFixed(2)}</span>
              </div>
              <div className="side-row">
                <span className="side-row-label">On Arrival</span>
                <span className="side-row-val">{currency}{(rounded - advance).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Trust badges */}
          <div className="side-card">
            <div className="side-head">Why Book With Us</div>
            <div className="trust-grid">
              {[
                { icon:"🔒", label:"SSL Secure Payment" },
                { icon:"✅", label:"Instant Confirmation" },
                { icon:"🚗", label:"Meet & Greet Service" },
                { icon:"📞", label:"24/7 Support" },
              ].map(t => (
                <div key={t.label} className="trust-item">
                  <div className="trust-icon">{t.icon}</div>
                  <div className="trust-label">{t.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* WhatsApp */}
          <a href="https://wa.me/255745593330" style={{
            display:"flex", alignItems:"center", justifyContent:"center", gap:10,
            padding:"12px", background:"transparent", border:"1px solid #25D366",
            color:"#25D366", fontFamily:"'Montserrat',sans-serif", fontSize:"0.62rem",
            letterSpacing:"0.15em", textTransform:"uppercase", textDecoration:"none",
            transition:"all 0.3s",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#25D366"; e.currentTarget.style.color = "#000"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#25D366"; }}
          >
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Book via WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}